import { useEffect, useRef, useState } from "react";
import { hasWarp, renderWarpedArea, type AreaShape, type AreaFit, type AreaWarp } from "../../lib/area-shape";
import { Loader2, Eraser, Brush, Trash2, Check, X, Undo2, PenTool, Eye, Square, Circle, CircleDot, Heart, Sun } from "lucide-react";
import { apiClient } from "../../lib/api";
import type { CustomMockup } from "@erc/shared";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001";
const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

type Tool = "brush" | "eraser" | "polygon" | "rect" | "ellipse" | "circle" | "heart";

const SHAPE_TOOLS: Tool[] = ["rect", "ellipse", "circle", "heart"];
const isShape = (t: Tool) => SHAPE_TOOLS.includes(t);

/** Sekli verilen kutuya gore ciz (path olusturur, fill/stroke cagirana ait) */
function shapePath(
  ctx: CanvasRenderingContext2D,
  kind: Tool,
  x0: number, y0: number, x1: number, y1: number
) {
  let x = Math.min(x0, x1), y = Math.min(y0, y1);
  let w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
  if (kind === "circle") {
    // Daire: kisa kenari baz al, baslangic noktasindan buyusun
    const d = Math.min(w, h);
    x = x0 < x1 ? x0 : x0 - d;
    y = y0 < y1 ? y0 : y0 - d;
    w = d; h = d;
  }
  ctx.beginPath();
  if (kind === "rect") {
    ctx.rect(x, y, w, h);
    return;
  }
  if (kind === "ellipse" || kind === "circle") {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    return;
  }
  // Kalp — ust iki kavis + asagi inen uc
  const cx = x + w / 2;
  const top = y + h * 0.3;
  ctx.moveTo(cx, top);
  ctx.bezierCurveTo(x, y, x, y + h / 2, cx, y + h);
  ctx.bezierCurveTo(x + w, y + h / 2, x + w, y, cx, top);
  ctx.closePath();
}

/**
 * Occlusion editoru — mockup'in tasarimin ONUNDE kalmasi gereken bolgelerini
 * (sac, katlanan kumasin ust kenari, kol) isaretler.
 *
 * Maske mockup cozunurlugunde ayri bir canvas'ta tutulur: BEYAZ = on planda
 * kalir, siyah = tasarim gorunur. Kaydederken maske sunucuya gider, sunucu
 * mockup'in kendi piksellerinden alfali bir "on plan" PNG'si uretir.
 */
export default function OcclusionEditor({
  mockup,
  designUrl,
  onClose,
  onSaved,
}: {
  mockup: CustomMockup;
  /** Ornek tasarim — hangi bolgenin kapanacagini gormek icin */
  designUrl?: string | null;
  onClose: () => void;
  onSaved: (updated: CustomMockup) => void;
}) {
  const W = mockup.imageWidth;
  const H = mockup.imageHeight;

  const maskRef = useRef<HTMLCanvasElement | null>(null);   // maske (W×H, gizli)
  const viewRef = useRef<HTMLCanvasElement | null>(null);   // ekranda gosterilen
  const bgRef = useRef<HTMLImageElement | null>(null);
  const designRef = useRef<HTMLImageElement | null>(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const undoStackRef = useRef<ImageData[]>([]);

  const [tool, setTool] = useState<Tool>("brush");
  const [brushSize, setBrushSize] = useState(Math.max(12, Math.round(W / 40)));
  const [feather, setFeather] = useState(mockup.occlusionFeather ?? 3);
  const [showMask, setShowMask] = useState(true);
  const [showDesign, setShowDesign] = useState(true);
  const [preview, setPreview] = useState(false);
  const [poly, setPoly] = useState<{ x: number; y: number }[]>([]);
  // Suruklenerek cizilen sekil (maske koordinatlarinda)
  const [shape, setShape] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Alanin EN PARLAK yuzde kaci on plana alinsin (mutlak esik acik renkli
  // urunde alanin tamamini gecip tasarimi tamamen yok ediyordu)
  const [brightPercent, setBrightPercent] = useState(12);

  // Ekran olcegi: canvas'i kutuya sigdir
  const viewW = 560;
  const scale = viewW / W;
  const viewH = Math.round(H * scale);

  // --- kurulum: mockup gorseli + varsa kayitli maske
  useEffect(() => {
    const mask = document.createElement("canvas");
    mask.width = W; mask.height = H;
    const mctx = mask.getContext("2d")!;
    mctx.fillStyle = "#000";
    mctx.fillRect(0, 0, W, H);
    maskRef.current = mask;

    let cancelled = false;
    const bg = new Image();
    bg.crossOrigin = "anonymous";
    bg.onload = () => {
      if (cancelled) return;
      bgRef.current = bg;
      if (mockup.maskUrl) {
        const m = new Image();
        m.crossOrigin = "anonymous";
        m.onload = () => {
          if (cancelled) return;
          mctx.drawImage(m, 0, 0, W, H);
          setReady(true); repaint();
        };
        m.onerror = () => { setReady(true); repaint(); };
        m.src = abs(mockup.maskUrl);
      } else {
        setReady(true); repaint();
      }
    };
    bg.onerror = () => { setError("Mockup görseli yüklenemedi"); setReady(true); };
    bg.src = abs(mockup.imageUrl);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockup.id]);

  /** Tasarim alanlari — hem eski tekil alan hem coklu dizi desteklenir */
  function areas() {
    const arr = (mockup.designAreas ?? []) as Array<{ x: number; y: number; w: number; h: number; rotation?: number; shape?: AreaShape; fit?: AreaFit; warp?: AreaWarp | null }>;
    if (arr.length > 0) return arr;
    if (mockup.designAreaW > 0 && mockup.designAreaH > 0) {
      return [{ x: mockup.designAreaX, y: mockup.designAreaY, w: mockup.designAreaW, h: mockup.designAreaH, rotation: mockup.designAreaRotation }];
    }
    return [];
  }

  /** Ornek tasarimi alanlarina "contain" olarak ciz (studio ile ayni mantik) */
  function drawDesign(ctx: CanvasRenderingContext2D) {
    const d = designRef.current;
    if (!d || !d.naturalWidth) return;
    for (const a of areas()) {
      // Carpitilmis alan: render ile ayni esleme (duz kutu yaniltiyordu)
      if (hasWarp(a)) {
        const r = renderWarpedArea(d, a);
        ctx.save();
        ctx.globalAlpha = (mockup.opacity ?? 100) / 100;
        ctx.drawImage(r.canvas, r.left * scale, r.top * scale, r.canvas.width * scale, r.canvas.height * scale);
        ctx.restore();
        continue;
      }
      const s = Math.min((a.w * scale) / d.naturalWidth, (a.h * scale) / d.naturalHeight);
      const w = d.naturalWidth * s, h = d.naturalHeight * s;
      const x = a.x * scale + (a.w * scale - w) / 2;
      const y = a.y * scale + (a.h * scale - h) / 2;
      ctx.save();
      if (a.rotation) {
        ctx.translate(a.x * scale, a.y * scale);
        ctx.rotate((a.rotation * Math.PI) / 180);
        ctx.translate(-a.x * scale, -a.y * scale);
      }
      ctx.globalAlpha = (mockup.opacity ?? 100) / 100;
      ctx.drawImage(d, x, y, w, h);
      ctx.restore();
    }
  }

  // --- ekrana ciz: mockup + tasarim + maske (veya gercek onizleme)
  function repaint() {
    const view = viewRef.current, mask = maskRef.current, bg = bgRef.current;
    if (!view || !mask) return;
    const ctx = view.getContext("2d")!;
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, view.width, view.height);
    if (bg) ctx.drawImage(bg, 0, 0, view.width, view.height);

    if ((showDesign || preview) && designRef.current) drawDesign(ctx);

    // ONIZLEME: maskeyi kirmizi gostermek yerine gercek sonucu ciz —
    // mockup'in maskelenmis pikselleri tasarimin ustune gelir
    if (preview) {
      if (bg) {
        const fg = document.createElement("canvas");
        fg.width = view.width; fg.height = view.height;
        const fctx = fg.getContext("2d")!;
        fctx.drawImage(mask, 0, 0, view.width, view.height);
        fctx.globalCompositeOperation = "source-in";
        fctx.drawImage(bg, 0, 0, view.width, view.height);
        ctx.drawImage(fg, 0, 0);
      }
      return;
    }

    if (showMask) {
      // Maskenin beyaz alanlarini yariseffaf kirmizi olarak goster
      const tmp = document.createElement("canvas");
      tmp.width = view.width; tmp.height = view.height;
      const tctx = tmp.getContext("2d")!;
      tctx.drawImage(mask, 0, 0, view.width, view.height);
      tctx.globalCompositeOperation = "source-in";
      tctx.fillStyle = "rgba(239,68,68,0.55)";
      tctx.fillRect(0, 0, view.width, view.height);
      ctx.drawImage(tmp, 0, 0);
    }

    // Suruklenen sekil onizlemesi
    if (shape && isShape(tool)) {
      ctx.save();
      ctx.strokeStyle = "#4f46e5"; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
      ctx.fillStyle = "rgba(79,70,229,0.20)";
      shapePath(ctx, tool, shape.x0 * scale, shape.y0 * scale, shape.x1 * scale, shape.y1 * scale);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // Devam eden poligon
    if (poly.length > 0) {
      ctx.save();
      ctx.strokeStyle = "#4f46e5"; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(poly[0].x * scale, poly[0].y * scale);
      for (const p of poly.slice(1)) ctx.lineTo(p.x * scale, p.y * scale);
      ctx.stroke();
      ctx.setLineDash([]);
      for (const p of poly) {
        ctx.fillStyle = "#4f46e5";
        ctx.beginPath(); ctx.arc(p.x * scale, p.y * scale, 3.5, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    }
  }

  useEffect(repaint, [showMask, showDesign, preview, poly, shape, tool, ready]);

  // Ornek tasarimi yukle
  useEffect(() => {
    if (!designUrl) { designRef.current = null; repaint(); return; }
    const d = new Image();
    d.crossOrigin = "anonymous";
    d.onload = () => { designRef.current = d; repaint(); };
    d.onerror = () => { designRef.current = null; repaint(); };
    d.src = abs(designUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designUrl]);

  function pushUndo() {
    const mask = maskRef.current;
    if (!mask) return;
    const ctx = mask.getContext("2d")!;
    undoStackRef.current.push(ctx.getImageData(0, 0, W, H));
    if (undoStackRef.current.length > 20) undoStackRef.current.shift();
  }

  function undo() {
    const prev = undoStackRef.current.pop();
    const mask = maskRef.current;
    if (!prev || !mask) return;
    mask.getContext("2d")!.putImageData(prev, 0, 0);
    repaint();
  }

  function toMaskCoords(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * W,
      y: ((e.clientY - rect.top) / rect.height) * H,
    };
  }

  function strokeTo(p: { x: number; y: number }) {
    const mask = maskRef.current;
    if (!mask) return;
    const ctx = mask.getContext("2d")!;
    ctx.strokeStyle = tool === "eraser" ? "#000" : "#fff";
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = brushSize;
    const from = lastPtRef.current ?? p;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
    repaint();
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (preview) return;
    const p = toMaskCoords(e);
    if (tool === "polygon") {
      setPoly((prev) => [...prev, p]);
      return;
    }
    if (isShape(tool)) {
      drawingRef.current = true;
      setShape({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      return;
    }
    pushUndo();
    drawingRef.current = true;
    lastPtRef.current = null;
    strokeTo(p);
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || tool === "polygon") return;
    const p = toMaskCoords(e);
    if (isShape(tool)) {
      setShape((s) => (s ? { ...s, x1: p.x, y1: p.y } : s));
      return;
    }
    strokeTo(p);
  }

  /** Suruklenen sekli maskeye bas */
  function commitShape() {
    const mask = maskRef.current;
    if (!mask || !shape) return;
    const tiny = Math.abs(shape.x1 - shape.x0) < 3 && Math.abs(shape.y1 - shape.y0) < 3;
    if (!tiny) {
      pushUndo();
      const ctx = mask.getContext("2d")!;
      ctx.fillStyle = "#fff";
      shapePath(ctx, tool, shape.x0, shape.y0, shape.x1, shape.y1);
      ctx.fill();
    }
    setShape(null);
    repaint();
  }

  function endStroke() {
    if (drawingRef.current && isShape(tool)) commitShape();
    drawingRef.current = false;
    lastPtRef.current = null;
  }

  /** Poligonu maskeye bas (doldur) */
  function commitPolygon() {
    const mask = maskRef.current;
    if (!mask || poly.length < 3) { setPoly([]); return; }
    pushUndo();
    const ctx = mask.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.moveTo(poly[0].x, poly[0].y);
    for (const p of poly.slice(1)) ctx.lineTo(p.x, p.y);
    ctx.closePath();
    ctx.fill();
    setPoly([]);
    repaint();
  }

  /** PARLAK YERLERI ISARETLE — tasarim ALANI icindeki en parlak %N piksel on
   *  plana alinir. Esik alanin kendi parlaklik dagilimindan hesaplanir; boylece
   *  koyu ve acik kumasta ayni sekilde calisir ve tum alani kaplamaz. */
  function markBrightAreas() {
    const mask = maskRef.current, bg = bgRef.current;
    if (!mask || !bg) return;
    const list = areas();
    if (list.length === 0) { setError("Önce tasarım alanı belirleyin — parlaklık yalnızca alan içinde taranır"); return; }
    setError(null);
    pushUndo();

    const src = document.createElement("canvas");
    src.width = W; src.height = H;
    src.getContext("2d")!.drawImage(bg, 0, 0, W, H);
    const data = src.getContext("2d")!.getImageData(0, 0, W, H).data;

    const inAny = (x: number, y: number) => list.some((a) => {
      let dx = x - a.x, dy = y - a.y;
      if (a.rotation) {
        const r = (-a.rotation * Math.PI) / 180;
        const c = Math.cos(r), sn = Math.sin(r);
        const nx = dx * c - dy * sn, ny = dx * sn + dy * c;
        dx = nx; dy = ny;
      }
      return dx >= 0 && dx <= a.w && dy >= 0 && dy <= a.h;
    });

    const hist = new Uint32Array(256);
    let total = 0;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (!inAny(x, y)) continue;
        const i = (y * W + x) * 4;
        const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        hist[Math.min(255, Math.max(0, Math.round(lum)))]++;
        total++;
      }
    }
    if (total === 0) { setError("Alan içinde piksel bulunamadı"); return; }
    const want = Math.max(1, Math.round((total * brightPercent) / 100));
    let acc = 0, cut = 255;
    for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc >= want) { cut = v; break; } }

    const add = document.createElement("canvas");
    add.width = W; add.height = H;
    const actx = add.getContext("2d")!;
    const out = actx.createImageData(W, H);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (!inAny(x, y)) continue;
        const i = (y * W + x) * 4;
        const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        if (lum < cut) continue;
        out.data[i] = 255; out.data[i + 1] = 255; out.data[i + 2] = 255; out.data[i + 3] = 255;
      }
    }
    actx.putImageData(out, 0, 0);

    const mctx = mask.getContext("2d")!;
    mctx.save();
    mctx.globalCompositeOperation = "lighter";
    mctx.drawImage(add, 0, 0);
    mctx.restore();
    repaint();
  }

  function clearAll() {
    const mask = maskRef.current;
    if (!mask) return;
    pushUndo();
    const ctx = mask.getContext("2d")!;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    setPoly([]);
    repaint();
  }

  /** Maske tamamen bos mu (hic isaret yok)? */
  function maskIsEmpty(): boolean {
    const mask = maskRef.current;
    if (!mask) return true;
    const d = mask.getContext("2d")!.getImageData(0, 0, W, H).data;
    for (let i = 0; i < d.length; i += 4 * 97) if (d[i] > 12) return false;  // seyrek tarama
    return true;
  }

  async function save() {
    const mask = maskRef.current;
    if (!mask) return;
    setSaving(true); setError(null);
    try {
      if (maskIsEmpty()) {
        const updated = await apiClient.clearMockupOcclusion(mockup.id);
        onSaved(updated);
        onClose();
        return;
      }
      const dataUrl = mask.toDataURL("image/png");
      const updated = await apiClient.saveMockupOcclusion(mockup.id, dataUrl, feather);
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }

  const toolBtn = (t: Tool, icon: React.ReactNode, label: string) => (
    <button
      onClick={() => { if (t !== "polygon") setPoly([]); setShape(null); setTool(t); }}
      className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium ${
        tool === t ? "bg-indigo-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"
      }`}
    >
      {icon}{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Ön Plan (Occlusion) Düzenleyici</h3>
            <p className="text-xs text-gray-500">
              Tasarımın <b>önünde</b> kalması gereken yerleri boya — saç, katlanan kumaşın üst kenarı, kol.
              Kırmızı alanlar tasarımı kapatır. {mockup.label}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          {toolBtn("brush", <Brush className="h-3.5 w-3.5" />, "Fırça")}
          {toolBtn("eraser", <Eraser className="h-3.5 w-3.5" />, "Silgi")}
          {toolBtn("polygon", <PenTool className="h-3.5 w-3.5" />, "Poligon")}
          {toolBtn("rect", <Square className="h-3.5 w-3.5" />, "Kare")}
          {toolBtn("ellipse", <Circle className="h-3.5 w-3.5" />, "Elips")}
          {toolBtn("circle", <CircleDot className="h-3.5 w-3.5" />, "Yuvarlak")}
          {toolBtn("heart", <Heart className="h-3.5 w-3.5" />, "Kalp")}
          <button onClick={undo} className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
            <Undo2 className="h-3.5 w-3.5" />Geri al
          </button>
          <button onClick={clearAll} className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" />Temizle
          </button>
          <label className="ml-auto flex items-center gap-1 text-xs text-gray-600">
            <input type="checkbox" checked={showMask} onChange={(e) => setShowMask(e.target.checked)} disabled={preview} />
            Maskeyi göster
          </label>
          {designUrl && (
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <input type="checkbox" checked={showDesign} onChange={(e) => setShowDesign(e.target.checked)} disabled={preview} />
              Tasarımı göster
            </label>
          )}
          <button
            onClick={() => setPreview((v) => !v)}
            title="Maskeyi kırmızı değil, gerçek sonuç olarak göster"
            className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium ${
              preview ? "bg-emerald-600 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            {preview ? "Düzenlemeye dön" : "Önizleme"}
          </button>
        </div>

        {!designUrl && (
          <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
            Örnek tasarım seçili değil — Mockup Yönetimi'ndeki "Test tasarımı" alanından bir görsel seçersen
            tasarımın nereye denk geldiğini burada görebilirsin.
          </p>
        )}

        {isShape(tool) && (
          <div className="mb-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
            Şekli çizmek için bastır-sürükle-bırak. “Yuvarlak” eşit kenarlı olur, “Elips” serbest.
          </div>
        )}

        {tool === "polygon" && (
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
            <span>Köşeleri tıkla, sonra “Poligonu uygula”. ({poly.length} nokta)</span>
            <button onClick={commitPolygon} disabled={poly.length < 3}
              className="ml-auto rounded bg-indigo-600 px-2 py-1 text-white disabled:opacity-40">
              Poligonu uygula
            </button>
            <button onClick={() => setPoly([])} className="rounded border border-indigo-200 px-2 py-1">Vazgeç</button>
          </div>
        )}

        {/* Otomatik: kumasin isik alan tepe noktalarini tek tikla on plana al */}
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <button
            onClick={markBrightAreas}
            title="Tasarım alanı içindeki parlak pikselleri ön plana al — tasarım o noktaların arkasında kalır"
            className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600"
          >
            <Sun className="h-3.5 w-3.5" /> Parlak yerleri işaretle
          </button>
          <label className="flex flex-1 items-center gap-2 text-xs text-amber-900" title="Düşük eşik daha çok yeri ön plana alır">
            En parlak %
            <input type="range" min={2} max={40} value={brightPercent}
              onChange={(e) => setBrightPercent(Number(e.target.value))} className="flex-1" />
            <span className="w-10 text-right">%{brightPercent}</span>
          </label>
          <span className="text-[10px] text-amber-700">Yalnızca tasarım alanı içinde çalışır; sonucu fırçayla düzeltebilirsin.</span>
        </div>

        <div className="mb-3 flex items-center gap-4">
          <label className="flex flex-1 items-center gap-2 text-xs text-gray-600">
            Fırça
            <input type="range" min={2} max={Math.round(W / 6)} value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))} className="flex-1" />
            <span className="w-10 text-right">{brushSize}px</span>
          </label>
          <label className="flex flex-1 items-center gap-2 text-xs text-gray-600" title="Kenar yumuşatma — 0 keskin kesim">
            Yumuşatma
            <input type="range" min={0} max={20} value={feather}
              onChange={(e) => setFeather(Number(e.target.value))} className="flex-1" />
            <span className="w-8 text-right">{feather}</span>
          </label>
        </div>

        <div className="relative mx-auto overflow-hidden rounded-lg border border-gray-200" style={{ width: viewW, height: viewH }}>
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
          <canvas
            ref={viewRef}
            width={viewW}
            height={viewH}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={endStroke}
            onMouseLeave={endStroke}
            className="cursor-crosshair"
            style={{ width: viewW, height: viewH, touchAction: "none" }}
          />
        </div>

        {error && <p className="mt-2 rounded bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}

        <div className="mt-4 flex items-center justify-between">
          <p className="text-[11px] text-gray-400">
            Bir kez çizersin, bu mockup ile üretilen tüm tasarımlarda geçerli olur.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
              Vazgeç
            </button>
            <button onClick={save} disabled={saving || !ready}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Kaydet ve ön planı üret
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
